import { ThemeContext } from '@benzinga/themetron';
import { formatNumberWithSuffix } from '@benzinga/visualization-utils';
import { WidgetContext } from '@benzinga/widget-tools';
import { EChartsOption } from 'echarts';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { FaDownload, FaInfoCircle, FaSync } from 'react-icons/fa';
import { IconType } from 'react-icons/lib';
import { useLogo } from '@benzinga/quotes-manager-hooks';
import { Logo } from '@benzinga/quotes-manager';
import { TooltipProps } from '../entity';
import { DateTime } from 'luxon';
import { getLabelConfig } from '../utils';

export const useConfig = (args, symbols, data, chartRef) => {
  const widgetContext = React.useContext(WidgetContext);
  const theme = React.useContext(ThemeContext);
  const [tooltip, setTooltip] = React.useState<TooltipProps>({ children: [], id: '', name: '', x: 0, y: 0 });
  const [logosMap, setLogosMap] = React.useState<Record<string, HTMLImageElement>>({});

  // echarts wipes the innerHTML of its tooltip on every mousemove, so the react portal renders into
  // this node instead: echarts only re-appends it, leaving the mounted tooltip subtree untouched.
  const tooltipHostRef = React.useRef<HTMLDivElement | null>(null);
  const [tooltipHost, setTooltipHost] = React.useState<HTMLDivElement | null>(null);
  const hoveredCellRef = React.useRef<string | undefined>(undefined);
  const isHostVisibleRef = React.useRef(false);

  const setHostVisibility = React.useCallback((visible: boolean) => {
    isHostVisibleRef.current = visible;
    if (tooltipHostRef.current) {
      tooltipHostRef.current.style.opacity = visible ? '1' : '0';
    }
  }, []);

  const getTooltipHost = React.useCallback(() => {
    if (!tooltipHostRef.current && typeof document !== 'undefined') {
      tooltipHostRef.current = document.createElement('div');
      setHostVisibility(false);
      setTooltipHost(tooltipHostRef.current);
    }
    return tooltipHostRef.current;
  }, [setHostVisibility]);

  React.useEffect(() => () => tooltipHostRef.current?.remove(), []);

  React.useEffect(() => {
    if (tooltipHost) {
      setHostVisibility(true);
    }
  }, [tooltip, tooltipHost, setHostVisibility]);

  const preserveFocusWithin = React.useCallback(() => {
    const host = tooltipHostRef.current;
    const focused = document.activeElement as (HTMLElement & Partial<HTMLTextAreaElement>) | null;

    if (!host || !isHostVisibleRef.current || !focused || !host.contains(focused)) {
      return;
    }

    const selectionStart = focused.selectionStart ?? null;
    const selectionEnd = focused.selectionEnd ?? null;

    queueMicrotask(() => {
      if (document.activeElement === focused || !focused.isConnected) {
        return;
      }
      focused.focus({ preventScroll: true });
      if (selectionStart !== null && selectionEnd !== null) {
        (focused as HTMLTextAreaElement).setSelectionRange(selectionStart, selectionEnd);
      }
    });
  }, []);

  const handleLogosUpdate = React.useCallback((newLogos: Logo[]) => {
    const preload = async () => {
      const map: Record<string, HTMLImageElement> = {};

      await Promise.all(
        newLogos.map(logo => {
          return new Promise<void>(resolve => {
            const url = logo?.files?.['mark_dark'];
            if (!logo?.search_key || !url) return resolve();

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              map[logo.search_key] = img;
              resolve();
            };
            img.onerror = () => resolve();
            img.src = url;
          });
        }),
      );

      setLogosMap(map);
    };

    if (newLogos && newLogos.length > 0) preload();
  }, []);

  useLogo(
    React.useMemo(() => symbols ?? [], [symbols]),
    {
      fields: 'mark_dark',
    },
    handleLogosUpdate,
  );

  const tooltipCoordinates = React.useCallback(
    (_pos, params, _dom, rect, _size) => {
      const [plotHeight, plotWidth] = [widgetContext.getClientHeight() ?? 0, widgetContext.getClientWidth() ?? 0];
      const [tooltipWidth, tooltipHeight] = params.data.children ? [500, 500] : [300, 300];
      const offset = 25;

      let tooltipX = rect.x + rect.width + offset;
      let tooltipY = rect.y;

      if (tooltipX + tooltipWidth + offset > plotWidth) {
        tooltipX = Math.max(0, rect.x - tooltipWidth - offset);
      }
      if (tooltipY + tooltipHeight > plotHeight) {
        tooltipY = Math.max(0, plotHeight - tooltipHeight);
      }
      if (tooltipX > plotWidth / 2) {
        tooltipX = tooltipX - tooltipWidth - offset;
      }
      if (tooltipY > plotHeight / 2) {
        tooltipY = tooltipY - tooltipHeight;
      }

      return [tooltipX, tooltipY];
    },
    [widgetContext],
  );

  const visualFormatter = React.useCallback(
    (value: number) => {
      const formattedValue = formatNumberWithSuffix(value, args.traces[0].color);
      return value < 0 ? `-${formattedValue}` : formattedValue;
    },
    [args.traces],
  );

  const iconToDataURL = React.useCallback((Icon: IconType, color = 'black'): string => {
    const svg = renderToStaticMarkup(React.createElement(Icon, { color }));
    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    return URL.createObjectURL(svgBlob);
  }, []);

  const decorateNode = React.useCallback(
    node => {
      const { children } = node;

      //computing max of local sector/industry (used for resizing)
      const visibleMaxValue = children?.length
        ? Math.max(...children.map(c => c.value?.[0] ?? 0))
        : (node.value?.[0] ?? 0);

      if (children && children.length > 0) {
        return {
          ...node,
          children: children.map(child =>
            decorateNode({
              ...child,
              visibleMaxValue,
            }),
          ),
        };
      }

      const logo = logosMap[node.name];
      const colorValue = node.value?.[1];
      const formattedColorValue =
        colorValue !== undefined ? formatNumberWithSuffix(colorValue, args.traces[0].color) : '--';
      const sizeValue = node.value?.[0] ?? 0;

      const computedLabelConfig = getLabelConfig(
        node.name,
        formattedColorValue,
        theme.colors.foreground,
        logo,
        true,
        sizeValue,
        node.visibleMaxValue ?? visibleMaxValue,
      );

      return {
        ...node,
        label: computedLabelConfig,
      };
    },
    [logosMap, args.traces, theme.colors.foreground],
  );

  const handleRefreshClick = React.useCallback(() => {
    if (chartRef.current) {
      const instance = chartRef.current.getEchartsInstance();
      instance.setOption(instance.getOption(), true);
    }
  }, [chartRef]);

  const options: EChartsOption = React.useMemo(
    () => ({
      series: [
        {
          bottom: '5%',
          breadcrumb: {
            emphasis: {
              itemStyle: {
                color: theme.colors.buttonPrimaryBackgroundHover,
              },
            },
            itemStyle: {
              borderWidth: 2,
              color: theme.colors.backgroundInactive,
            },
            left: '1%',
            show: true,
            top: '2%',
          },
          data: data.data.map(decorateNode),
          emphasis: {
            focus: 'series',
          },
          height: '88%',
          levels: [
            {
              itemStyle: {
                borderColor: theme.colors.border,
              },
              upperLabel: {
                show: false,
              },
            },
            {
              emphasis: {
                itemStyle: {
                  borderColor: theme.colors.buttonPrimaryBackgroundHover,
                },
              },
              itemStyle: {
                borderColor: theme.colors.border,
                borderWidth: 2,
                gapWidth: 2,
              },
              upperLabel: {
                color: theme.colors.foreground,
                ellipsis: '...',
                fontSize: 15,
                fontWeight: 'bold',
                overflow: 'truncate',
                show: true,
              },
            },
            {
              emphasis: {
                itemStyle: {
                  borderColor: theme.colors.buttonPrimaryBackgroundHover,
                },
              },
              itemStyle: {
                borderColor: theme.colors.border,
                borderWidth: 1,
              },
              upperLabel: {
                color: theme.colors.foregroundInactive,
                ellipsis: '...',
                fontSize: 11,
                fontStyle: 'italic',
                fontWeight: 'bold',
                overflow: 'truncate',
                show: true,
              },
            },
          ],
          name: 'Heatmap',
          roam: true,
          type: 'treemap',
          upperLabel: {
            color: theme.colors.foreground,
            formatter: params => {
              return `${params.name} - ${formatNumberWithSuffix(params.value?.[0], args.traces[0].display)}`;
            },
            show: true,
          },
          width: '98%',
        },
      ],
      textStyle: {
        ellipsis: '...',
        fontFamily: 'Nimbus Sans, helvetica, arial, sans-serif',
        overflow: 'truncate',
      },
      toolbox: {
        feature: {
          myInfoBtn: {
            icon: `image://${iconToDataURL(FaInfoCircle, theme.colors.foregroundActive)}`,
            iconStyle: {
              borderColor: theme.colors.foregroundActive,
              borderWidth: 2,
            },
            onclick: () => undefined,
            title: '',
          },
          myResetBtn: {
            icon: `image://${iconToDataURL(FaSync, theme.colors.foregroundActive)}`,
            iconStyle: {
              borderColor: theme.colors.foregroundActive,
              borderWidth: 2,
            },
            onclick: handleRefreshClick,
            show: true,
            title: '',
          },
          //eslint-disable-next-line
          myDownloadImage: {
            icon: `image://${iconToDataURL(FaDownload, theme.colors.foregroundActive)}`,
            iconStyle: {
              borderColor: theme.colors.foregroundActive,
              borderWidth: 2,
            },
            name: `Heatmap_${DateTime.now().toFormat('yyyy-MM-dd_hh_mm_ss_a')}.png`,
            onclick: function () {
              const chartInstance = chartRef.current?.getEchartsInstance?.();
              if (!chartInstance) return;

              const dataURL = chartInstance.getDataURL({
                backgroundColor: theme.colors.backgroundActive,
                pixelRatio: 2,
                type: 'png',
              });

              const a = document.createElement('a');
              a.download = `Heatmap_${DateTime.now().toFormat('yyyy-MM-dd_hh_mm_ss_a')}.png`;
              a.href = dataURL;
              a.click();
            },
            show: true,
            title: '',
            type: 'png',
          },
        },
        itemGap: 20,
        itemSize: 13,
        right: 25,
        show: true,
        tooltip: {
          backgroundColor: theme.colors.backgroundActive,
          extraCssText: `
            max-width: 150px;
            display: flex;
            flex-direction: column;
            white-space: normal;
            word-wrap: break-word;
            padding: 5px;
            box-sizing: border-box;
          `,
          formatter: params => {
            const messages = {
              myDownloadImage: 'Download',
              myInfoBtn:
                'Note: Use mouse wheel to zoom in and out. Drag zoomed map to pan it & clicking on the ticker node opens up the details widget with that ticker',
              myResetBtn: 'Reset Zoom',
            };
            const message = messages[params.name] ?? '';
            return `<span style="color: ${theme.colors.foregroundActive}">${message}</span>`;
          },
          show: true,
          textStyle: {
            color: theme.colors.brandForeground,
            fontSize: 12,
          },
        },
        top: '1%',
      },
      tooltip: {
        border: 0,
        formatter: ((params: { dataIndex: number; name: string; data: { children?: unknown[] } }) => {
          const host = getTooltipHost();
          const cell = `${params.dataIndex}_${params.name}`;

          if (hoveredCellRef.current === cell) {
            preserveFocusWithin();
          } else {
            hoveredCellRef.current = cell;
            setHostVisibility(false);
            setTooltip({
              children: params.data.children as TooltipProps['children'],
              id: `heatMap_widget_${params.name}`,
              name: params.name ?? '',
            });
          }
          return host ?? '';
        }) as never,
        padding: 0,
        position: tooltipCoordinates,
      },
      visualMap: {
        bottom: 'bottom',
        dimension: 1,
        formatter: (min: number | string | Date | undefined | null) => visualFormatter(Number(min)),
        inRange: {
          color: ['#f63538', '#414554', '#30cc5a'],
        },
        itemHeight: 300,
        itemWidth: 13,
        max: isNaN(data.max) ? 100 : data.max,
        min: isNaN(data.min) ? 0 : data.min,
        orient: 'horizontal',
        right: 'center',
        text: ['High', 'Low'],
        textStyle: {
          color: theme.colors.foreground,
          fontStyle: 'italic',
        },
        type: 'continuous',
      },
    }),
    [
      theme.colors.buttonPrimaryBackgroundHover,
      theme.colors.backgroundInactive,
      theme.colors.border,
      theme.colors.foreground,
      theme.colors.foregroundInactive,
      theme.colors.foregroundActive,
      theme.colors.backgroundActive,
      theme.colors.brandForeground,
      data.data,
      data.max,
      data.min,
      decorateNode,
      iconToDataURL,
      handleRefreshClick,
      tooltipCoordinates,
      visualFormatter,
      args.traces,
      chartRef,
      getTooltipHost,
      preserveFocusWithin,
      setHostVisibility,
    ],
  );

  return { options, tooltip, tooltipHost };
};
